import { API_ENDPOINTS, OLYMP_API_BASE_URL } from '../config/api';

const COHORTS_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.COHORTS.LIST}`.replace(/\/$/, '');
const FETCH_TIMEOUT_MS = 10000;

function buildCohortUrl(path = '') {
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  if (!normalizedPath) {
    return `${COHORTS_BASE}/`;
  }
  return `${COHORTS_BASE}/${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

function buildHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function cohortFetch(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers: buildHeaders(),
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });

    if (!response.ok) {
      const error = new Error(
        response.status === 401
          ? 'Authentication required. Please sign in again.'
          : `Cohort API error ${response.status}`
      );
      error.status = response.status;
      error.url = url;
      throw error;
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function normalizeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeCohortSummary(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    id: item.id,
    name: item.name || '',
    description: item.description || '',
    status: item.status || 'unedited',
    parentId: item.parentId ?? null,
    childCount: Number(item.childCount || 0),
    parameterListId: item.parameterListId || null,
    parameterListName: item.parameterListName || '',
    isPublic: Boolean(item.isPublic),
    patientCount: item.patientCount ?? null,
    usageCount: Number(item.usageCount || 0),
    createdAt: item.createdAt || null,
    updatedAt: item.updatedAt || null,
    children: normalizeArray(item.children).map(normalizeCohortSummary).filter(Boolean),
  };
}

function normalizeCohortDetail(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    ...normalizeCohortSummary(item),
    createdBy: item.createdBy || '',
    owner: item.owner || '',
    graph: item.graph || null,
  };
}

export async function listCohorts() {
  const payload = await cohortFetch(buildCohortUrl());
  const results = normalizeArray(payload?.results ?? payload).map(normalizeCohortSummary).filter(Boolean);
  return {
    count: Number(payload?.count ?? results.length),
    results,
  };
}

export async function getCohort(id) {
  const payload = await cohortFetch(buildCohortUrl(id));
  return normalizeCohortDetail(payload);
}

export async function createCohort(body = {}) {
  const payload = await cohortFetch(buildCohortUrl(), {
    method: 'POST',
    body,
  });
  return normalizeCohortDetail(payload);
}

export async function saveCohortGraph(id, graph) {
  const payload = await cohortFetch(buildCohortUrl(`${id}/graph`), {
    method: 'POST',
    body: { graph },
  });
  return normalizeCohortDetail(payload);
}

export async function duplicateCohort(id) {
  const payload = await cohortFetch(buildCohortUrl(`${id}/duplicate`), {
    method: 'POST',
  });
  return normalizeCohortDetail(payload);
}

export async function deleteCohort(id) {
  await cohortFetch(buildCohortUrl(id), {
    method: 'DELETE',
  });
}

const EXECUTION_NODE_STATUSES = new Set(['completed', 'error', 'unmapped']);

function normalizeExecutionNodeResult(item) {
  if (!item || typeof item !== 'object' || !item.nodeId) {
    return null;
  }

  let status = EXECUTION_NODE_STATUSES.has(item.status) ? item.status : 'error';
  let message = item.message || '';
  // Kein Rueckfall auf eine geschaetzte Zahl (AK2): nur ein 'completed'-Knoten
  // traegt einen Wert, alles andere bleibt null. 'completed' ohne verwertbare
  // Zahl (Feld fehlt, ist ein String, oder ein unterdruecktes Aggregat) ist ein
  // Vertragsbruch der Antwort -- kein stilles null bei sonst gruenem Status,
  // sondern 'error' mit Begruendung (Review Runde 1, B1).
  let patientCount = status === 'completed' && Number.isFinite(item.patientCount) ? item.patientCount : null;
  if (status === 'completed' && patientCount === null) {
    status = 'error';
    message = message || 'Server reported a completed node without a usable patient count.';
  }

  return {
    nodeId: String(item.nodeId),
    status,
    patientCount,
    message,
  };
}

// Kein Rueckfall auf 0 ms, wenn `gesamt` fehlt oder keine Zahl ist -- eine 0
// waere eine Messung, die es nicht gab (#795 AK3). `uebersetzung`/`datenbank`
// sind unabhaengig davon einzeln optional: fehlt nur die Aufteilung, bleibt
// die Gesamtdauer trotzdem anzeigbar.
function normalizeDauerMs(value) {
  if (!value || typeof value !== 'object' || !Number.isFinite(value.gesamt)) {
    return null;
  }
  return {
    gesamt: value.gesamt,
    uebersetzung: Number.isFinite(value.uebersetzung) ? value.uebersetzung : null,
    datenbank: Number.isFinite(value.datenbank) ? value.datenbank : null,
  };
}

function normalizeExecutionResult(payload) {
  return {
    executedAt: payload?.executedAt || null,
    // Kein Rueckfall auf 'omop': fehlt das Feld, ist die Quelle unbekannt --
    // der Aufrufer zeigt das an, statt das angefragte Backend als Ist-Zustand
    // auszugeben (Review Runde 1, B4).
    backend: typeof payload?.backend === 'string' && payload.backend ? payload.backend : null,
    nodes: normalizeArray(payload?.nodes).map(normalizeExecutionNodeResult).filter(Boolean),
    dauerMs: normalizeDauerMs(payload?.dauerMs),
  };
}

/**
 * Die uebersetzte Abfrage zu einem Kohortenknoten (#796, Frontend-Haelfte von
 * #789).
 *
 * **Drei Dinge weichen hier vom Rest des Dienstes ab, alle mit Grund:**
 *
 * 1. *Kein `cohortFetch`.* Der wirft bei jedem Nicht-200 eine Meldung der Form
 *    "Cohort API error 422" und verwirft den Rumpf. Genau dort steht aber die
 *    Begruendung, die angezeigt werden soll -- der Download verweigert sich
 *    mit 422, wenn auch nur ein Kriterium unuebersetzbar ist (#794), und das
 *    ist auf hospital1 heute der Regelfall, nicht die Ausnahme.
 * 2. *Feldnamen in snake_case.* Der Endpunkt antwortet mit `JsonResponse` statt
 *    ueber den DRF-Renderer, damit die Ticketfeldnamen unveraendert ankommen.
 *    Wer hier `kriterienUebersetzt` liest, findet nichts.
 * 3. *Kein Zeitlimit von 10 s.* Das Uebersetzen beruehrt keine Zieldatenbank
 *    (#789 AK1) und ist schnell; der Abruf teilt trotzdem das Limit des
 *    Dienstes, damit eine haengende Instanz nicht die Seite blockiert.
 */
export async function getCohortQuery(id, { backend = 'omop' } = {}) {
  const url = `${buildCohortUrl(`${id}/query`)}?backend=${encodeURIComponent(backend)}`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(),
      signal: controller.signal,
    });
    const rumpf = await response.json().catch(() => null);
    if (response.ok) {
      return {
        backend: rumpf?.backend || backend,
        sprache: rumpf?.sprache || '',
        abfrage: rumpf?.abfrage || '',
        uebersetzt: Number(rumpf?.kriterien_uebersetzt || 0),
        unuebersetzbar: normalizeArray(rumpf?.unuebersetzbar),
        source: 'api',
      };
    }
    // Auch der abgelehnte Fall traegt Inhalt: `detail` sagt warum,
    // `unuebersetzbar` sagt welches Kriterium. Beides gehoert angezeigt.
    return {
      backend,
      sprache: '',
      abfrage: '',
      uebersetzt: 0,
      unuebersetzbar: normalizeArray(rumpf?.unuebersetzbar),
      detail: rumpf?.detail || `Cohort API error ${response.status}`,
      status: response.status,
      source: 'error',
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * Dieselbe Abfrage als Datei (#796 AK3/AK4).
 *
 * Liefert bei Erfolg `{ text, dateiname }` -- den Dateinamen nimmt der Aufrufer
 * aus `Content-Disposition`, nicht aus einer eigenen Erfindung: das Backend
 * setzt dort Kohortenname und Zeitstempel, und zwei Quellen fuer denselben
 * Namen waeren zwei Wahrheiten.
 *
 * Bei 422 kommt **kein** Fehler, sondern `{ detail }`. Eine Verweigerung mit
 * Begruendung ist eine Aussage ueber die Kohorte, kein Ausfall der Anwendung
 * (#794): die Datei waere unvollstaendig, und das soll man lesen koennen.
 */
export async function downloadCohortQuery(id, { backend = 'omop' } = {}) {
  const url = `${buildCohortUrl(`${id}/query`)}?backend=${encodeURIComponent(backend)}&download=1`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(),
      signal: controller.signal,
    });
    if (!response.ok) {
      const rumpf = await response.json().catch(() => null);
      return {
        detail: rumpf?.detail || `Cohort API error ${response.status}`,
        unuebersetzbar: normalizeArray(rumpf?.unuebersetzbar),
        status: response.status,
        source: 'error',
      };
    }
    const text = await response.text();
    return { text, dateiname: dateinameAusKopf(response.headers), source: 'api' };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/** Dateiname aus `Content-Disposition`, oder leer, wenn der Kopf fehlt. */
function dateinameAusKopf(headers) {
  const kopf = headers?.get?.('Content-Disposition') || '';
  const treffer = /filename="?([^";]+)"?/i.exec(kopf);
  return treffer ? treffer[1] : '';
}

export async function executeCohort(id, { targetNodeId = null, mode = 'full', backend = 'omop' } = {}) {
  const payload = await cohortFetch(buildCohortUrl(`${id}/execute`), {
    method: 'POST',
    body: { targetNodeId, mode, backend },
  });
  return normalizeExecutionResult(payload);
}
